import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
  renderPublicHero,
} from "../../../components/general/publicPageStyles";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../../constants/brandConstants";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

type HoursSavedRow = {
  workflow: string;
  manualTime: string;
  withAfhHub: string;
  timeSaved: string;
};

const BASE_HOURS_SAVED_ROWS: HoursSavedRow[] = [
  {
    workflow: "Negotiated Care Plan (NCP)",
    manualTime: "5-10 hours",
    withAfhHub: "First Draft: < 3 min",
    timeSaved: "Saves 4-8+ hours",
  },
  {
    workflow: "Nurse Assessment, Care Plan & Delegation Forms",
    manualTime: "1-1.5 days",
    withAfhHub: "First Draft: < 5 min",
    timeSaved: "Saves an entire workday",
  },
  {
    workflow: "Behavior & Interventions care plan",
    manualTime: "1-2 hours",
    withAfhHub: "First Draft: < 1 minute",
    timeSaved: "Saves nearly all manual documentation time",
  },
  {
    workflow: "CBHS/IBSS Tracker & Monthly Reports",
    manualTime: "1-3 hours of tracking and report writing",
    withAfhHub: "Track daily, monthly report in seconds",
    timeSaved: "Cuts routine behavior documentation time dramatically",
  },
  {
    workflow: "eMAR Setup & Medication List Import",
    manualTime: "30-90 minutes of retyping and cleanup",
    withAfhHub: "Import and review in minutes",
    timeSaved: "Avoids repetitive medication entry work",
  },
  {
    workflow: "ADL Tracking & Daily Reports",
    manualTime: "30-90 minutes of daily tracking and report preparation",
    withAfhHub: "Chart daily, export reports in seconds",
    timeSaved: "Reduces repeated ADL documentation work",
  },
  {
    workflow: "Podiatrist - Foot and Nail Care",
    manualTime: "30-60 minutes of manual documentation, follow-up and reminders",
    withAfhHub: "Document and review in minutes",
    timeSaved: "Cuts podiatrist care documentation time significantly",
  },
  {
    workflow: "Preliminary Service Plan",
    manualTime: "1-2 hours",
    withAfhHub: "First Draft: < 1 minute",
    timeSaved: "Reduces hours to seconds",
  },
  {
    workflow: "Facility Website",
    manualTime: "Hours to days with separate tools",
    withAfhHub: "Website live < 5 minutes",
    timeSaved: "Launch faster, avoid domain charges and content update in seconds",
  },
];

export const getHoursSavedRows = (brandKey: string, websiteLabel: string) =>
  BASE_HOURS_SAVED_ROWS.flatMap((row) => {
    if (brandKey === BRAND_KEY_ONE_HUB_FOR_CARE) {
      if (row.workflow === "Negotiated Care Plan (NCP)") {
        return [{ ...row, workflow: "Generate Care Plan documents" }];
      }

      if (["Behavior & Interventions", "Preliminary Service Plan", "Facility Website"].includes(row.workflow)) {
        return [];
      }
    }

    if (row.workflow === "Facility Website") {
      return [{ ...row, workflow: websiteLabel }];
    }

    return [row];
  });

const ctaLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  borderRadius: "14px",
  fontWeight: 700,
  padding: "0.95rem 1.15rem",
} as const;

const WhyAfhHub = () => {
  const authExperience = useAuthExperience();
  const mobile = useResponsive().isMobile;
  const hoursSavedRows = getHoursSavedRows(authExperience.brandKey, authExperience.discovery.jobWebsiteLabel);
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash === "#emar-workflow") {
      navigate(RouterAddress.EMAR_PAGE, { replace: true });
    }
  }, [navigate]);

  return (
    <div style={publicPageContainerStyle}>
      <div style={{ textAlign: "center" as const }}>
        {renderPublicHero(
          "Why Use Hub",
          "Turn manual resident documentation into faster workflows.",
          `${authExperience.brandName} is designed to reduce repetitive paperwork, organize care-team workflow, and help providers and nurses move faster with assessment, care plan, and delegation form automation.`
        )}
      </div>

      <div className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center" }}>
        <div
          className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
          style={{ background: "#fff7ed", border: "1px solid #f1dbc7", color: "#9a3412", fontWeight: 700 }}
        >
          <i className="pi pi-clock" />
          {`Hours Saved with ${authExperience.brandName}`}
        </div>
        <h2 style={{ color: "#0f3d63", marginTop: 0, fontSize: mobile ? "1.5rem" : "1.9rem" }}>
          What once took hours of paperwork, assessments, and podiatrist documentation now happens in minutes
        </h2>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "760px" }}>
          {`These estimates reflect the difference between building resident documentation manually versus using ${authExperience.brandName} automation, CBHS/IBSS Tracker behavior documentation, monthly behavior reports, podiatrist foot and nail care workflow support, and connected workflow tools.`}
        </p>

        {mobile ? (
          <div className="mt-4 flex flex-column gap-3" style={{ textAlign: "left" }}>
            {hoursSavedRows.map((row) => (
              <div
                key={row.workflow}
                style={{
                  border: "1px solid #dde9f5",
                  borderRadius: "18px",
                  padding: "1rem",
                  background: "#ffffff",
                }}
              >
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1rem" }}>{row.workflow}</div>
                <div className="mt-3" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.6rem" }}>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>Traditional Manual Time</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{row.manualTime}</div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>{`With ${authExperience.brandName}`}</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{row.withAfhHub}</div>
                  </div>
                  <div>
                    <div style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", fontWeight: 700 }}>Estimated Time Saved</div>
                    <div style={{ color: "#166534", fontWeight: 800 }}>{row.timeSaved}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4" style={{ overflowX: "auto", textAlign: "left" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
              <thead>
                <tr style={{ background: "#f8fbff" }}>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Workflow</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Traditional Manual Time</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>{`With ${authExperience.brandName}`}</th>
                  <th style={{ textAlign: "left", padding: "0.9rem 1rem", borderBottom: "1px solid #dde9f5", color: "#0f3d63" }}>Estimated Time Saved</th>
                </tr>
              </thead>
              <tbody>
                {hoursSavedRows.map((row) => (
                  <tr key={row.workflow}>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#0f172a", fontWeight: 700 }}>{row.workflow}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#475569" }}>{row.manualTime}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#185da2", fontWeight: 700 }}>{row.withAfhHub}</td>
                    <td style={{ padding: "1rem", borderBottom: "1px solid #eef3f8", color: "#166534", fontWeight: 800 }}>{row.timeSaved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3" style={{ ...publicMutedTextStyle, fontSize: "0.92rem", maxWidth: "760px", marginLeft: "auto", marginRight: "auto" }}>
          Actual time can vary by resident complexity, source documents, and review needs, but the goal stays the same: cut down manual writing and speed up completion.
        </div>
      </div>

      <div id="emar-workflow" className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center", scrollMarginTop: mobile ? "7rem" : "8rem" }}>
        <div
          className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
          style={{ background: "#eff6ff", border: "1px solid #dbeafe", color: "#1d4ed8", fontWeight: 700 }}
        >
          <i className="pi pi-list-check" />
          eMAR Workflow
        </div>
        <div
          className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
          style={{ background: "#eefbf3", border: "1px solid #b7e4c7", color: "#166534", fontWeight: 700 }}
        >
          <i className="pi pi-check-circle" />
          No pharmacy integration required
        </div>
        <h2 style={{ color: "#0f3d63", marginTop: 0, fontSize: mobile ? "1.5rem" : "1.9rem" }}>
          Start eMAR faster without pharmacy integration delays
        </h2>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "780px" }}>
          {`${authExperience.brandName} helps teams import MAR sheets from any pharmacy, build the active medication list, add vital trackers, compare the latest MAR sheet with the current med list, and move into charting faster.`}
        </p>

        <div className="grid mt-4 text-left">
          {[
            {
              title: "Add from care plan",
              description: "Use the medication section already saved in the resident care plan to start the eMAR list faster.",
            },
            {
              title: "Add from MAR sheet",
              description: "Upload a MAR sheet and let the system parse the medication details into a cleaner starting point for review.",
            },
            {
              title: "Add from assessment",
              description: "Use the resident assessment and related source details when medication information is already documented there.",
            },
            {
              title: "Compare MAR Sheet with Active Med List",
              description: "Review the latest MAR sheet against the current active medication list to spot differences that need follow-up and help support compliance review.",
            },
          ].map((item) => (
            <div key={item.title} className="col-12 md:col-6 xl:col-3">
              <div
                style={{
                  height: "100%",
                  border: "1px solid #dbe7f3",
                  borderRadius: "18px",
                  padding: "1.15rem",
                  background: "#ffffff",
                }}
              >
                <div style={{ color: "#0f3d63", fontWeight: 800, fontSize: "1rem", marginBottom: "0.45rem" }}>{item.title}</div>
                <div style={{ ...publicMutedTextStyle, margin: 0 }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ ...publicMutedTextStyle, margin: "1rem auto 0", maxWidth: "780px" }}>
          The goal is not to skip review. It is to give the care team a faster, more organized medication starting point, make differences easier to catch, and support compliant resident-specific checks.
        </p>
        <div className="mt-4">
          <a
            href={RouterAddress.EMAR_PAGE}
            rel="noopener noreferrer"
            className="px-4 py-3 border-round-lg"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.55rem",
              background: "#0f3d63",
              color: "#ffffff",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            <i className="pi pi-external-link" />
            Explore eMAR Features
          </a>
        </div>
      </div>

      <div id="adl-workflow" className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center", scrollMarginTop: mobile ? "7rem" : "8rem" }}>
        <div
          className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
          style={{ background: "#f3fbf5", border: "1px solid #cfe8d6", color: "#166534", fontWeight: 700 }}
        >
          <i className="pi pi-check-square" />
          ADL Tracking
        </div>
        <h2 style={{ color: "#0f3d63", marginTop: 0, fontSize: mobile ? "1.5rem" : "1.9rem" }}>
          Capture daily care with clearer ADL records
        </h2>
        <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "780px" }}>
          {`${authExperience.brandName} helps staff record what happened for each activity of daily living, including the support provided and item-specific tracking details, without relying on separate paper lists.`}
        </p>

        <div className="grid mt-4 text-left">
          {[
            {
              title: "Daily pass workflow",
              description: "Review scheduled ADL tasks, record actions such as Done, Refused, or Not Recorded, and keep the status visible for the care team.",
            },
            {
              title: "Support and parameters",
              description: "Use the resident's support level and the right tracking parameter for the item, such as completion, amount, size, or number of times.",
            },
            {
              title: "Care-team access",
              description: "Providers manage the setup while authorized staff chart assigned residents through the same resident-centered workflow.",
            },
            {
              title: "Reports for review",
              description: "Export daily or monthly ADL reports with recorded values, staff initials, action colors, support-level indicators, and notes.",
            },
          ].map((item) => (
            <div key={item.title} className="col-12 md:col-6 xl:col-3">
              <div
                style={{
                  height: "100%",
                  border: "1px solid #cfe8d6",
                  borderRadius: "18px",
                  padding: "1.15rem",
                  background: "#ffffff",
                }}
              >
                <div style={{ color: "#166534", fontWeight: 800, fontSize: "1rem", marginBottom: "0.45rem" }}>{item.title}</div>
                <div style={{ ...publicMutedTextStyle, margin: 0 }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ ...publicMutedTextStyle, margin: "1rem auto 0", maxWidth: "780px" }}>
          ADL tracking keeps routine personal-care and mobility documentation consistent, easier to review, and connected to the resident's care plan.
        </p>
      </div>

      <div className="mt-3" style={{ ...publicSectionCardStyle, textAlign: "center" }}>
        <div className="grid align-items-center justify-content-center">
          <div className="col-12 lg:col-8">
            <div
              className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-3"
              style={{ background: "#f3fbf5", border: "1px solid #cfe8d6", color: "#166534", fontWeight: 700 }}
            >
              <i className="pi pi-check-circle" />
              Decision Support
            </div>
            <h2 style={{ color: "#0f3d63", marginTop: 0, fontSize: mobile ? "1.45rem" : "1.85rem" }}>
              {`If your team is spending too much time on paperwork, ${authExperience.brandName} is built to reduce that load`}
            </h2>
            <p style={{ ...publicMutedTextStyle, margin: "0.75rem auto 0", maxWidth: "760px" }}>
              {`${authExperience.brandName} brings assessment automation, care plan drafting, delegation form automation, CBHS/IBSS Tracker behavior documentation, monthly behavior reports, resident records, and reminders into one connected workflow so less effort gets wasted on repetitive manual work.`}
            </p>
          </div>
          <div className="col-12 lg:col-6">
            <div className="flex flex-column gap-3 align-items-center" style={{ width: "100%", maxWidth: mobile ? "100%" : "420px", margin: "0 auto" }}>
              <a
                href={RouterAddress.SIGN_UP}
                rel="noopener noreferrer"
                style={{
                  ...ctaLinkStyle,
                  background: "#0f3d63",
                  color: "#ffffff",
                  width: "100%",
                }}
              >
                {`Start Using ${authExperience.brandName}`}
              </a>
              <a
                href={RouterAddress.PRICING_PAGE}
                rel="noopener noreferrer"
                style={{
                  ...ctaLinkStyle,
                  background: "#ffffff",
                  color: "#0f3d63",
                  border: "1px solid #d7e6f4",
                  width: "100%",
                }}
              >
                Explore Pricing
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyAfhHub;
